import { InferType, array, boolean, number, object, string } from 'yup';
import { TimestampValidator } from 'validations';
import { PUZZLE_IMAGE, PUZZLE_TYPES } from 'app-constants';
import { resolutionStringValidator } from 'validations/string';
import { Status } from 'shared/enums';

export const PuzzleDTOBaseSchema = object({
  Publisher: string().required(),
  aiGenerated: boolean().required(),
  collections: array().of(string().required()).min(1).required(),
  createdAt: TimestampValidator().required(),
  publishedAt: TimestampValidator().nullable(),
  url: string().url().required(),
  iconURL: string().url().required(),
  keywords: string().required(),
  likes: number().required(),
  name: string().required(),
  plays: number().required(),
  premiumTier: number().required(),
  status: string<
    'pending' | 'edited' | 'approved' | 'rejected' | 'disabled' | 'deleted' | 'processing'
  >()
    .matches(/pending|edited|approved|rejected|disabled|deleted|processing/)
    .required(),
  rejectMessage: string().when('status', {
    is: Status.Rejected,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
});

export const PuzzleDTOSchema = PuzzleDTOBaseSchema.shape({
  type: string<typeof PUZZLE_TYPES['static']>()
    .matches(/static/)
    .required(),
  physicalDistribution: boolean().required(),
  printUrl: string().url().nullable(),
  lowQualityImageUrl: string().url().required(),
  highQualityImageUrl: string().url().nullable(),
  _meta: object({
    original: string().required(),
    originalResolution: resolutionStringValidator().required(),
    resolution: resolutionStringValidator({
      expectedWidth: PUZZLE_IMAGE.DIGITAL.CROP.WIDTH,
      expectedHeight: PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT,
    }).required(),
    highQualityResolution: resolutionStringValidator({
      expectedWidth: PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH,
      expectedHeight: PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT,
    }).nullable(),
    lowQualityResolution: resolutionStringValidator({
      expectedWidth: PUZZLE_IMAGE.DIGITAL.CROP_LQ.WIDTH,
      expectedHeight: PUZZLE_IMAGE.DIGITAL.CROP_LQ.HEIGHT,
    }).required(),
    digitalCropData: object({
      x: number().min(0).required(),
      y: number().min(0).required(),
      width: number().min(PUZZLE_IMAGE.DIGITAL.CROP.WIDTH).required(),
      height: number().min(PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT).required(),
      rotation: number().min(0).max(359).required(),
    }).required(),
    previousCollection: string().when('status', {
      is: Status.Rejected,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  }).required(),
});

export const VideoPuzzleDTOSchema = PuzzleDTOBaseSchema.shape({
  type: string<typeof PUZZLE_TYPES['video']>().matches(/video/).required(),
  videoIconUrl: string().url().required(),
  videoPreviewPlaceholderUrl: string().url().required(),
  videoPreviewUrl: string().url().required(),
  _meta: object({
    originalResolution: resolutionStringValidator().required(),
    previewResolution: resolutionStringValidator().required(),
    resolution: resolutionStringValidator().required(),
    original: string().required(),
    previousCollection: string()
      .when('status', {
        is: Status.Rejected,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      })
      .required(),
  }),
});

export type VideoPuzzleDTO = InferType<typeof VideoPuzzleDTOSchema>;
export type PuzzleDTO = InferType<typeof PuzzleDTOSchema>;
export type AllPuzzleDTO = VideoPuzzleDTO | PuzzleDTO;
